/**
	* App View
	* Handles: root component, popups
	* @module App
*/

/// Imports

// React & JQuery
import React, {useState, useEffect} from 'react';
import $ from 'jquery';

// OpenLayers
import OlMap from 'ol/Map';
import DragPan from 'ol/interaction/DragPan';
import ZoomCtl from 'ol/control/Zoom';
import ZoomSliderCtl from 'ol/control/ZoomSlider';
import MouseWheelZoom from 'ol/interaction/MouseWheelZoom';
import DoubleClickZoom from 'ol/interaction/DoubleClickZoom';

// Models
import Vector2 from 'lib/classes/vector2';
import MapTypes from 'lib/interfaces/maptypes';
import { Popup, drawPopups } from 'lib/classes/popup';
import MapToolMode from 'lib/enums/maptoolmode';

// Views
import MapContainer from 'views/MapContainer';
import Tools from 'views/Tools';
import Attributions from 'views/Attributions';
import OverviewMap from 'views/OverviewMap';

// UI
import EntryPopup from 'views/popups/Entry';
import Guide from 'views/popups/Guide';
import DisclaimerPopup from 'views/popups/Disclaimer';

// Tools
import { getWindowDimensions, parseURLs } from 'lib/utilities/misc';
import { getMap } from 'config/map';
import { getToolModeByName, getToolEnabled } from 'lib/utilities/toolmodes';

// Lang
import { getLocaleString } from 'lib/localization/vendor';

// Styles
import 'styles/css/App.scss';

/// Variable declarations
var initialized:boolean = false;

function App() {
	// State hooks
	const [popupsOpen, setPopupsOpen] = useState<Popup[]>([]);
	const [dimensions, setDimensions] = useState(getWindowDimensions());
	const [olMap, setOlMap] = useState<OlMap>();
	const [overviewMap, setOverviewMap] = useState<OlMap>();
	const [viewerReady, setViewerReady] = useState<boolean>(false);
	const [mapConfig, setMapConfig] = useState<any>({});
	const [accessStyling, setAccessStyling] = useState<boolean>(_config.accessStylingEnabled);
	
	// Get styled tooltip from UI manager
	const StyledTooltip = _ui.tooltip;
	
	// Called when the map instance changes
	useEffect(() => {
		if(olMap) {
			setTimeout(() => checkForGuide(), 1000);
			setTimeout(() => checkForDisclaimer(), 100);
		}
	}, [olMap]);
	
	// Called when map control tool toggled
	useEffect(() => {
		if(!olMap)
			return;
			
		const mapControlEnabled:boolean = getToolEnabled(MapToolMode.MapControl, mapConfig);
		const dragPan:any = olMap.getInteractions().getArray().find(interaction => interaction instanceof DragPan);
		const mwZoom:any = olMap.getInteractions().getArray().find(interaction => interaction instanceof MouseWheelZoom);
		const dcZoom:any = olMap.getInteractions().getArray().find(interaction => interaction instanceof DoubleClickZoom);
		const zoomCtl:any = olMap.getControls().getArray().find(ctl => ctl instanceof ZoomCtl);
		
		if(mapControlEnabled) {
			if(!dragPan)
				olMap.getInteractions().extend([new DragPan()]);
			
			if(!mwZoom)
				olMap.getInteractions().extend([new MouseWheelZoom()]);
				
			if(!dcZoom)
				olMap.getInteractions().extend([new DoubleClickZoom()]);
			
			//if(!zoomCtl)
			//	olMap.getControls().extend([new ZoomCtl()]);
		}else{
			if(dragPan)
				olMap.getInteractions().remove(dragPan);
			
			if(mwZoom)
				olMap.getInteractions().remove(mwZoom);
			
			if(dcZoom)
				olMap.getInteractions().remove(dcZoom);
				
			//if(zoomCtl)
			//	olMap.getControls().remove(zoomCtl);
		}
		
	}, [olMap, getToolEnabled(MapToolMode.MapControl, mapConfig)]);
	
	/**
		Initializes the module
	*/
	const initialize = () => {
		setViewerReady(false);
		
		window.addEventListener('resize', onWindowResize);
		onWindowResize(); // define _dimensions
		
		// Global popup event handled only by this view
		_events.on('show-popups', (popups:Popup[]) => {
			setPopupsOpen([...popups]);
		});
		
		// Mouse move UI handler
		$(document).mousemove(function(e) {
			const pos:Vector2 = new Vector2(e.pageX, e.pageY);
			
			_ui.mousePosition = pos;
			_events.invoke('mouse-move', pos);
		});
		
		// Video wrappers
		_events.on('resize-video-wrappers', () => {
			$('.video-wrapper').each(function() {
				var $this = $(this);
				var $parent = $this.parent();
				var parentWidth = $parent.width();
				
				$this.css('height', parentWidth * 0.5625); // Set the height to 56.25% of the width (16:9 aspect ratio)
			});
		});
		$(document).ready(function() {
			const call = () => _events.invoke('resize-video-wrappers');
			call(); // Call the function on page load
			$(window).resize(call); // Bind the function to the resize event
		});
		
		/**
			* Map viewer ready event handler
			* 
			* @param clientId - id of opus client
			* @param mapId - id of opus map
		*/
		_events.on('map-viewer-ready', () => {
			const mapId:number = _config.mapId;
			const clientId:number = _config.clientId;
			
			getMap(mapId, clientId, (maps:MapTypes) => {
				const map:OlMap = maps.map;
				const overviewMap:OlMap = maps.overviewMap;
				
				if(!map) {
					/*_ui.showError(`Could not load map id ${mapId}. Continue to try a different one.`, '', () => {
						EntryPopup.set('mapId', mapId);
						EntryPopup.set('clientId', clientId);
						_ui.showPopup(EntryPopup);
						return false;
					});*/
					_ui.showError(`Could not load map id ${mapId} with client id ${clientId}.`);
					return;
				}
				
				_ui.closePopup();
				
				startNewMap(map, overviewMap);
			});
		});
		
		/**
			* Map viewer new config ready
			* 
			* @param {any} mapConfig - New map config object
			* @remarks
			* Sets the new map config to the map as a property.
			* Then updates the state hook with map config to reload DOM.
		*/
		_events.on('new-map-config', (map:OlMap, mapConfig:any) => {
			map.set('opus-config', mapConfig);
			
			setMapConfig(mapConfig);
		});
		
		/**
			* Accessibility toggle event
			* 
			* @param {boolean} enabled - Should accessibility styling be enabled
		*/
		_events.on('toggle-access-styling', (enabled:boolean) => {
			_config.accessStylingEnabled = enabled;
			setAccessStyling(enabled);
			
			if(enabled) {
				localStorage.setItem('omv-access', 'true');
			}else{
				localStorage.removeItem('omv-access');
			}
		});
		
		// Adjust resolution for higher res displays
		adjustResolution();
		
		// Load the data attributes
		loadDataAttributes();
		
		initialized = true;
	}
	
	const adjustResolution = () => {
		/*
			Adjust for higher resolution displays
		*/
		/*const res:number = dimensions.width * dimensions.height;
		const resLimit:number = 1336320;
		
		let zoom:number = res / resLimit * 0.5;
		
		if(zoom > 2) zoom = 2;
		
		$('body').css('zoom', zoom < 1 ? 1 : zoom);*/
		
		const res:number = dimensions.width * dimensions.height;
		const resLimit:number = 1336320;
		
		/*if(res > resLimit)
			_ui.showNotice(getLocaleString('reswarning'));*/
	}
	
	/**
		* Start new map
		* 
		* @param {OlMap} map - OpenLayers map to use
	*/
	const startNewMap = (map:OlMap, overviewMap:OlMap) => {
		// Reset the URL hash
		history.replaceState(undefined, undefined, '');
		
		// Reset the ol map container on the dom
		$('#olmapcontainer').html('');
		
		// Assign needed global properties to map
		map.set('opus-layer-groups', []);
		
		// Call global event saying a new map has been started
		_events.invoke('new-map-started', map);
		
		// Set the new map in the component state and re-render
		setOlMap(map);
		setOverviewMap(overviewMap);
		setMapConfig(map.get('opus-config'));
		setViewerReady(true);
	}
	
	/**
		* Check if the guide needs showing and if it does, show it
	*/
	const checkForGuide = () => {
		if(!getToolEnabled(MapToolMode.Tutorial, mapConfig))
			return;
		
		const guideShown:boolean = !!localStorage.getItem('omv-guide-shown');
		
		if(guideShown) return;
		
		Guide.set('map', olMap);
		
		_ui.showPopup(Guide);
	}
	
	/**
		* Check if the disclaimer needs showing and if it does, show it
	*/
	const checkForDisclaimer = () => {
		if(!mapConfig?.disclaimer)
			return;
		
		DisclaimerPopup.set('map-config', mapConfig);
		
		_ui.showPopup(DisclaimerPopup);
	}
	
	const loadDataAttributes = () => {
		_ui.showLoading();
		
		const element = $('#opusmap');
		
		_config.assetURL = element.data('assets') ?? '';
		_config.mapId = element.data('map-id') ?? 0;
		_config.clientId = element.data('client-id') ?? 0;
		_config.storageId = element.data('storage-id') ?? 0;
		_config.UUID = element.data('site-id') ?? _config.UUID; // site id can be overridden by attribute
		_config.showLayers = element.data('show-layers') ?? _config.showLayers;
		_config.showAllLayersOnStart = element.data('show-all-layers-start') ?? _config.showAllLayersOnStart;
		_config.defaultLayers = element.data('default-layers')?.split(',');
		_config.pinDropEnabled = element.data('pin-drop') ?? _config.pinDropEnabled;
		_config.queryLayersEnabled = element.data('query-layers') ?? _config.queryLayersEnabled;
		_config.toolsEnabledOverride = element.data('tools-enabled')?.split(',') ?? _config.toolsEnabledOverride;
		_config.toolsDisabledOverride = element.data('tools-disabled')?.split(',') ?? _config.toolsDisabledOverride;
		_config.drawToolsOverride = element.data('draw-tools')?.split(',') ?? _config.drawToolsOverride;
		_config.existingSitesSource = element.data('existing-sites');
		_config.existingSitesCallback = element.data('existing-sites-callback');
		_config.featureDrawnCallback = element.data('feature-drawn-callback');
		_config.showOpusAttribution = element.data('show-attribution') ?? true;
		_config.focusFeaturesOnLoad = element.data('focus-features') ?? _config.focusFeaturesOnLoad;
		_config.intersectionChecking = element.data('intersection-check') ?? _config.intersectionChecking;
		_config.existingSitesIntersectionChecking = element.data('es-intersection-check') ?? _config.existingSitesIntersectionChecking;
		_config.defaultScaleLineUnits = element.data('scale-line-units') ?? _config.defaultScaleLineUnits;
		_config.defaultToolMode = getToolModeByName(element.data('default-tool'));
		_config.featureVariationsEnabled = element.data('feature-variations-enabled') ?? _config.featureVariationsEnabled;
		_config.featureVariationsSource = element.data('feature-variations-source');
		_config.localeSource = element.data('locale-source');
		_config.previewOrigin = element.data('preview-origin');
		_config.annotationView = element.data('annotation-view') ?? _config.annotationView;
		_config.screenshotMap = element.data('screenshot-map') ?? _config.screenshotMap;
		_config.screenshotWidth = element.data('screenshot-width') ?? _config.screenshotWidth;
		_config.screenshotHeight = element.data('screenshot-height') ?? _config.screenshotHeight;
		_config.accessStylingEnabled = localStorage.getItem('omv-access') ?? element.data('access') ?? _config.accessStylingEnabled;
		
		if(_config.localeSource)
			_config.localeSource = _config.localeSource.split('$').join(element.data('locale'));
		
		_config.setLang( element.data('locale') || 'en-GB' );
		_config.localeSource && _config.lang.loadOverrideSource(_config.localeSource);
		
		setAccessStyling(_config.accessStylingEnabled);
		
		// Use a timeout so the loading box shows and the user doesn't think nothing is happening
		setTimeout(() => _events.invoke('map-viewer-ready'), 10);
	}
	
	/**
		* Window resize handler
		* Sets dimensions globally on _dimensions for use in other modules
	*/
	const onWindowResize = () => {
		const newDimensions:any = getWindowDimensions();
		
		setDimensions(newDimensions);
		
		global._dimensions = newDimensions;
	}
	
	/*
		Draw map dependent items
	*/
	const drawMapDependentItems = () => {
		if(olMap == null || !viewerReady) return;
		
		return (
			<>
				<MapContainer olMap={olMap} />
				<OverviewMap olMap={olMap} overviewMap={overviewMap} />
				<Attributions olMap={olMap} />
				<Tools olMap={olMap} />
			</>
		)
	}
	
	if(!initialized) initialize();
	
	return (
		<>
			{drawMapDependentItems()}
			<StyledTooltip place='bottom' />
			<>
				{drawPopups(popupsOpen)}
			</>
		</>
	)
}

export default App;